import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Copy, Check, ExternalLink, Image as ImageIcon, FileText, Link2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SharePreviewCard - Shows how a blog post will appear when shared on social media
 * Displays LinkedIn and Twitter/X preview mockups with actual post data
 */
const SharePreviewCard = ({ post, baseUrl = window.location.origin }) => {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedShareUrl, setCopiedShareUrl] = useState(false);

  if (!post) return null;

  // Build URLs
  const postUrl = `${baseUrl}/blog/${post.slug}`;
  const shareUrl = `${baseUrl}/api/blog/share/${post.slug}`;
  
  // Get image URL - ensure absolute
  const imageUrl = post.og_image_url || post.featured_image_url || '';
  const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl}`;
  
  // Meta content
  const ogTitle = post.seo_title || post.title || 'Untitled';
  const ogDescription = post.seo_description || post.excerpt || '';
  const displayDescription = ogDescription.length > 160 
    ? ogDescription.slice(0, 157) + '...' 
    : ogDescription;

  const handleCopyUrl = async (url, type) => {
    try {
      await navigator.clipboard.writeText(url);
      if (type === 'share') {
        setCopiedShareUrl(true);
        setTimeout(() => setCopiedShareUrl(false), 2000);
      } else {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }
      toast.success('URL copied to clipboard');
    } catch (e) {
      toast.error('Failed to copy URL');
    }
  };

  const openLinkedInInspector = () => {
    window.open(`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const openTwitterValidator = () => {
    window.open(`https://cards-dev.twitter.com/validator`, '_blank');
  };

  return (
    <Card data-testid="share-preview-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ExternalLink className="w-5 h-5" />
          Share Preview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Social Preview Tabs */}
        <Tabs defaultValue="linkedin" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
            <TabsTrigger value="twitter">Twitter/X</TabsTrigger>
          </TabsList>

          {/* LinkedIn Preview */}
          <TabsContent value="linkedin" className="mt-4">
            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
              {/* LinkedIn Card Mock */}
              <div className="bg-[#f3f2ef] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[#0a66c2] flex items-center justify-center text-white font-bold text-sm">
                    C
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-gray-900">CBAIQ</div>
                    <div className="text-xs text-gray-500">Just now</div>
                  </div>
                </div>
              </div>
              
              {/* Preview Card */}
              <div className="border-t">
                {absoluteImageUrl ? (
                  <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                    <img 
                      src={absoluteImageUrl} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-full items-center justify-center bg-gray-200">
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[1.91/1] bg-gray-200 flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <div className="p-3 bg-[#f3f2ef]">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    cbaiq.com
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">
                    {ogTitle}
                  </h4>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Twitter Preview */}
          <TabsContent value="twitter" className="mt-4">
            <div className="border rounded-2xl overflow-hidden bg-white shadow-sm">
              {/* Twitter Card Mock */}
              {absoluteImageUrl ? (
                <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                  <img 
                    src={absoluteImageUrl} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full items-center justify-center bg-gray-200">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                </div>
              ) : (
                <div className="aspect-[1.91/1] bg-gray-200 flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <div className="p-3 border-t">
                <h4 className="font-normal text-[15px] text-gray-900 leading-tight line-clamp-2">
                  {ogTitle}
                </h4>
                <p className="text-[13px] text-gray-500 mt-1 line-clamp-2">
                  {displayDescription}
                </p>
                <div className="flex items-center gap-1 mt-2 text-gray-500">
                  <Link2 className="w-3 h-3" />
                  <span className="text-[13px]">cbaiq.com</span>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Meta Data Box */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Meta Data
          </h4>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">og:title:</span>
              <span className="ml-2 font-mono text-xs bg-background px-1.5 py-0.5 rounded">
                {ogTitle}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">og:description:</span>
              <span className="ml-2 font-mono text-xs bg-background px-1.5 py-0.5 rounded line-clamp-1">
                {displayDescription || '(empty)'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">og:image:</span>
              {absoluteImageUrl ? (
                <span className="ml-2 font-mono text-xs bg-background px-1.5 py-0.5 rounded line-clamp-1">
                  {absoluteImageUrl}
                </span>
              ) : (
                <Badge variant="outline" className="ml-2 text-xs">Not set</Badge>
              )}
            </div>
          </div>
        </div>

        {/* URLs Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Post URL:</span>
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
              {postUrl}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopyUrl(postUrl, 'post')}
              className="shrink-0"
            >
              {copiedUrl ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Share URL:</span>
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
              {shareUrl}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopyUrl(shareUrl, 'share')}
              className="shrink-0"
            >
              {copiedShareUrl ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Validator Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={openLinkedInInspector}
            className="flex-1"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            LinkedIn Inspector
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openTwitterValidator}
            className="flex-1"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Twitter Validator
          </Button>
        </div>

        {/* Status indicator */}
        {post.status !== 'published' && (
          <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            <span>⚠️</span>
            <span>Post is not published. Social previews will only work for published posts.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SharePreviewCard;
